import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Home, Users, FileSignature, CreditCard, Wrench,
  Tag, MessageSquare, BarChart3, Settings as Cog, ShieldCheck,
  Menu, X, LogOut, Bell, ChevronRight, Building2, ClipboardList,
  ClipboardCheck, Wallet, Receipt, Zap, AlertTriangle, Send, Inbox, ShieldAlert, UserCog, Megaphone, Trash2
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { roleLabel, formatDateTime } from "../lib/format";
import api from "../lib/api";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/units", label: "Units", icon: Home },
    { to: "/tenants", label: "Tenants", icon: Users },
    { to: "/leads", label: "Public Enquiries", icon: Inbox },
    { to: "/leases", label: "Leases", icon: FileSignature },
    { to: "/payments", label: "Payments", icon: CreditCard },
    { to: "/arrears", label: "Arrears & Anomalies", icon: AlertTriangle },
    { to: "/pricing", label: "Pricing", icon: Tag },
    { to: "/inspections", label: "Inspections", icon: ClipboardCheck },
    { to: "/refunds", label: "Caution Refunds", icon: Wallet },
    { to: "/utility-bills", label: "Utility Bills", icon: Zap },
    { to: "/expenses", label: "Expenses", icon: Receipt },
    { to: "/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/communications", label: "Communications", icon: Send },
    { to: "/feedback", label: "Feedback", icon: MessageSquare },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/audit-logs", label: "Audit Logs", icon: ShieldCheck },
    { to: "/staff", label: "User Access", icon: Users },
    { to: "/security-alerts", label: "Security Feed", icon: ShieldAlert },
    { to: "/promo-banner", label: "Promo Banner", icon: Megaphone },
    { to: "/lease-recycle-bin", label: "Recycle Bin", icon: Trash2 },
    { to: "/settings", label: "Settings", icon: Cog },
  ],
  manager: [
    { to: "/manager", label: "Dashboard", icon: LayoutDashboard },
    { to: "/units", label: "Units", icon: Home },
    { to: "/tenants", label: "Tenants", icon: Users },
    { to: "/leads", label: "Public Enquiries", icon: Inbox },
    { to: "/leases", label: "Leases", icon: FileSignature },
    { to: "/payments", label: "Payments", icon: CreditCard },
    { to: "/arrears", label: "Arrears & Anomalies", icon: AlertTriangle },
    { to: "/pricing", label: "Pricing", icon: Tag },
    { to: "/inspections", label: "Inspections", icon: ClipboardCheck },
    { to: "/refunds", label: "Caution Refunds", icon: Wallet },
    { to: "/utility-bills", label: "Utility Bills", icon: Zap },
    { to: "/expenses", label: "Expenses", icon: Receipt },
    { to: "/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/communications", label: "Communications", icon: Send },
    { to: "/feedback", label: "Feedback", icon: MessageSquare },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/security-alerts", label: "Security Feed", icon: ShieldAlert },
    { to: "/promo-banner", label: "Promo Banner", icon: Megaphone },
  ],
  lawyer: [
    { to: "/legal", label: "Dashboard", icon: LayoutDashboard },
    { to: "/leases", label: "Leases", icon: FileSignature },
    { to: "/tenants", label: "Tenants", icon: Users },
    { to: "/pricing", label: "Pricing", icon: Tag },
    { to: "/communications", label: "Communications & Notices", icon: Send },
    { to: "/reports", label: "Reports", icon: BarChart3 },
  ],
  tenant: [
    { to: "/tenant", label: "My Dashboard", icon: LayoutDashboard },
    { to: "/tenant/lease", label: "My Lease", icon: FileSignature },
    { to: "/tenant/payments", label: "Payments", icon: CreditCard },
    { to: "/tenant/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/tenant/onboarding", label: "Profile", icon: ClipboardList },
    { to: "/tenant/feedback", label: "Feedback", icon: MessageSquare },
    { to: "/security-alerts", label: "Security Feed", icon: ShieldAlert },
  ],
  maintenance: [
    { to: "/maintenance-staff", label: "My Tickets", icon: Wrench },
  ],
};

export default function Layout({ children }) {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
      } catch {}
    };
    fetchNotifs();
    const i = setInterval(fetchNotifs, 30000);
    return () => clearInterval(i);
  }, [user]);

  if (!user) return null;
  const items = NAV_BY_ROLE[user.role] || [];
  const unread = notifs.filter(n => !n.read_status).length;
  const logo = settings?.logo_url || "https://customer-assets.emergentagent.com/job_b1b732c2-f76b-4b34-905f-135af01bf27f/artifacts/60bo7zef_Copilot_20260617_182205.png";

  const Sidebar = (
    <aside className="sidebar-texture relative flex flex-col w-72 bg-[#0E5C3A] text-white h-full overflow-y-auto" data-testid="sidebar">
      <div className="px-6 py-6 border-b border-[#0A4A2E] flex items-center gap-3">
        <div className="bg-white rounded-xl p-1.5 flex-shrink-0">
          <img src={logo} alt="Modav Homes" className="w-10 h-10 object-contain" />
        </div>
        <div>
          <div className="font-serif text-lg font-bold leading-tight">Modav Homes</div>
          <div className="text-[11px] text-[#C9A227] italic tracking-wide">Comfort & Convenience</div>
        </div>
      </div>
      <nav className="flex-1 px-4 py-4 space-y-1" data-testid="sidebar-nav">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid={`nav-${to.replace(/\//g, "-").slice(1)}`}>
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-[#0A4A2E]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center font-bold text-white">
            {user.full_name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user.full_name}</div>
            <div className="text-[11px] text-white/60">{roleLabel(user.role)}</div>
          </div>
        </div>
        <button className="sidebar-link w-full" onClick={() => { setMobileOpen(false); navigate("/profile"); }} data-testid="my-profile-link">
          <UserCog size={16}/> My Profile
        </button>
        <button className="sidebar-link w-full" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button">
          <LogOut size={16}/> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-[#F7F9F8] overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-shrink-0">{Sidebar}</div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10">{Sidebar}</div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-[#E5EBE8] px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden btn-ghost" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 text-sm text-[#5E7166]">
              <Building2 size={16} className="text-[#0E5C3A]" />
              <span className="hidden sm:inline">Modav Homes</span>
              <ChevronRight size={14} />
              <span className="font-semibold text-[#1A2E24]">{items.find(i => location.pathname.startsWith(i.to))?.label || ""}</span>
            </div>
            {settings?.environment && settings.environment !== "production" && (
              <span
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#C92A2A] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shadow-sm"
                title="You are viewing the preview environment. Data here is sandboxed and never touches production."
                data-testid="env-badge"
              >
                <AlertTriangle size={10} />
                Preview Data
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 relative">
            <button className="btn-ghost relative" onClick={() => setNotifOpen(!notifOpen)} data-testid="notifications-btn">
              <Bell size={18}/>
              {unread > 0 && <span className="absolute -top-1 -right-1 bg-[#C92A2A] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{unread}</span>}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-12 w-80 card-modav p-0 z-50 max-h-96 overflow-y-auto" data-testid="notifications-panel">
                <div className="px-4 py-3 border-b border-[#E5EBE8] flex items-center justify-between sticky top-0 bg-white z-10">
                  <div className="font-semibold text-sm">Notifications {notifs.length > 0 && <span className="text-[10px] text-[#5E7166] ml-1">({notifs.length})</span>}</div>
                  <div className="flex items-center gap-2">
                    {unread > 0 && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post("/notifications/mark-all-read");
                            setNotifs(notifs.map(n => ({ ...n, read_status: true })));
                          } catch {}
                        }}
                        className="text-[10px] text-[#0E5C3A] hover:underline font-bold uppercase tracking-wider"
                        data-testid="notif-mark-all-read"
                      >
                        Mark all read
                      </button>
                    )}
                    {notifs.some(n => n.read_status) && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post("/notifications/clear-read");
                            setNotifs(notifs.filter(n => !n.read_status));
                          } catch {}
                        }}
                        className="text-[10px] text-[#C92A2A] hover:underline font-bold uppercase tracking-wider"
                        title="Delete already-read notifications"
                        data-testid="notif-clear-read"
                      >
                        Clear read
                      </button>
                    )}
                    <button onClick={() => setNotifOpen(false)} className="text-[#5E7166] hover:text-[#1A2E24]"><X size={16}/></button>
                  </div>
                </div>
                {notifs.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-[#5E7166]">No notifications</div>
                ) : notifs.map(n => (
                  <div key={n.id} className={`px-4 py-3 border-b border-[#E5EBE8] last:border-0 group relative ${!n.read_status ? "bg-[#FDF6E3]/40" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{n.title}</div>
                        <div className="text-xs text-[#5E7166] mt-1">{n.message}</div>
                        <div className="text-[10px] text-[#5E7166]/70 mt-1">{formatDateTime(n.created_at)}</div>
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!n.read_status && (
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/notifications/${n.id}/read`);
                                setNotifs(notifs.map(x => x.id === n.id ? { ...x, read_status: true } : x));
                              } catch {}
                            }}
                            className="text-[10px] text-[#0E5C3A] hover:underline"
                            title="Mark as read"
                            data-testid={`notif-read-${n.id}`}
                          >
                            Read
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await api.delete(`/notifications/${n.id}`);
                              setNotifs(notifs.filter(x => x.id !== n.id));
                            } catch {}
                          }}
                          className="text-[10px] text-[#C92A2A] hover:underline"
                          title="Clear this notification"
                          data-testid={`notif-clear-${n.id}`}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pb-24 md:pb-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
